import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  FitnessCenter as LessonIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

const LocationProfitStatistics = ({ locationData, formatCurrency }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [viewMode, setViewMode] = useState({});

  if (!locationData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  const handleViewMode = (locId, mode) => {
    if (mode !== null) {
      setViewMode((prev) => ({ ...prev, [locId]: mode }));
    }
  };

  const profitColor = (val) => (val >= 0 ? "success.main" : "error.main");
  const profitBg = (val) =>
    val >= 0
      ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
      : "linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%)";

  const totalRevenue = (locationData.total_single_entry_revenue || 0) + (locationData.total_pass_revenue || 0);

  return (
    <>
      {/* Warnings */}
      {locationData.warnings && locationData.warnings.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Uwagi:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {locationData.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Unattributed pass revenue notice */}
      {locationData.unattributed_pass_revenue > 0 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Przychód z karnetów nieprzypisany do lokalizacji:{" "}
          <strong>{formatCurrency(locationData.unattributed_pass_revenue)}</strong> — karnety wielolokalizacyjne bez
          zarejestrowanego użycia w tym okresie.
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MoneyIcon sx={{ color: "white", mr: 1 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Przychód łączny
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {formatCurrency(totalRevenue)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block" }}>
                  Jednorazowe: {formatCurrency(locationData.total_single_entry_revenue)} ({locationData.total_single_entry_count} wejść)
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block" }}>
                  Karnety: {formatCurrency(locationData.total_pass_revenue)} ({locationData.total_pass_attendances} wejść)
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mt: 0.5, fontWeight: 600 }}>
                  PayU: {formatCurrency(locationData.total_paid_via_payu)}
                </Typography>
                {locationData.total_debt_cleared_amount > 0 && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", pl: 1.5 }}>
                    ↳ w tym z długu: {formatCurrency(locationData.total_debt_cleared_amount)} ({locationData.total_debt_cleared_count} wejść)
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", fontWeight: 600 }}>
                  Gotówka: {formatCurrency(locationData.total_paid_cash)}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", fontWeight: 600 }}>
                  Admin: {formatCurrency(locationData.total_admin_cleared_amount)} ({locationData.total_admin_cleared_count} wejść)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PersonIcon sx={{ color: "white", mr: 1 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Koszty trenerów
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {formatCurrency(locationData.total_trainer_cost)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LocationIcon sx={{ color: "white", mr: 1 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Koszty lokalizacji
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {formatCurrency(locationData.total_location_cost)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", background: profitBg(locationData.total_profit) }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <TrendingUpIcon sx={{ color: "white", mr: 1 }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Zysk łączny
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {formatCurrency(locationData.total_profit)}
              </Typography>
              {locationData.unattributed_pass_revenue > 0 && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", mt: 0.5 }}>
                  + {formatCurrency(locationData.unattributed_pass_revenue)} nieprzypisanych
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Per-location accordions */}
      {locationData.locations.length === 0 && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">Brak danych o lokalizacjach w wybranym okresie</Typography>
        </Paper>
      )}

      {locationData.locations.map((loc) => {
        const mode = viewMode[loc.location_id] || "lessons";
        const locTotalRevenue = (loc.single_entry_revenue || 0) + (loc.pass_revenue || 0);
        return (
          <Accordion key={loc.location_id} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, width: "100%" }}>
                <LocationIcon sx={{ color: "text.secondary", mr: 0.5 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1, minWidth: 120 }}>
                  {loc.location_name}
                </Typography>
                <Chip label={`${loc.lesson_count} zajęć`} size="small" />
                <Chip label={`${loc.single_entry_count} jednorazowych`} size="small" variant="outlined" />
                <Chip label={`${loc.pass_attendances} karnetem`} size="small" variant="outlined" />
                <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Typography variant="body2" color="text.secondary">
                    Przychód: <strong>{formatCurrency(locTotalRevenue)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Koszty: <strong>{formatCurrency(loc.total_cost)}</strong>
                  </Typography>
                  <Chip
                    label={formatCurrency(loc.profit)}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: loc.profit >= 0 ? "success.light" : "error.light",
                      color: loc.profit >= 0 ? "success.dark" : "error.dark",
                    }}
                  />
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {/* Location summary row */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Jednorazowe</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.single_entry_revenue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {loc.single_entry_count} wejść
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Przychód z karnetów</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.pass_revenue)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Koszt trenera</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.trainer_cost)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Koszt lokalizacji</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.location_cost)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Zysk</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: profitColor(loc.profit) }}>
                      {formatCurrency(loc.profit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Online (PayU)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.paid_via_payu)}
                    </Typography>
                    {loc.debt_cleared_amount > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        ↳ w tym z długu: {formatCurrency(loc.debt_cleared_amount)}
                        {loc.debt_cleared_count > 0 ? ` (${loc.debt_cleared_count} wejść)` : ""}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Gotówka (user)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.paid_cash)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Typography variant="caption" color="text.secondary">Oczyszczone przez admina</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(loc.admin_cleared_amount)}
                    </Typography>
                    {loc.admin_cleared_count > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {loc.admin_cleared_count} wejść
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* Toggle per-lesson / per-trainer */}
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, v) => handleViewMode(loc.location_id, v)}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="lessons">
                  <LessonIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Per zajęcia
                </ToggleButton>
                <ToggleButton value="trainers">
                  <PersonIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Per trener
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Per lesson type table */}
              {mode === "lessons" && (
                <TableContainer>
                  <Table size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rodzaj zajęć</TableCell>
                        <TableCell align="right">Zajęcia</TableCell>
                        <TableCell align="right">Jed. (PLN)</TableCell>
                        <TableCell align="right">Jed. (szt.)</TableCell>
                        <TableCell align="right">Karnety (PLN)</TableCell>
                        <TableCell align="right">Karnety (szt.)</TableCell>
                        <TableCell align="right">Koszt trenera</TableCell>
                        <TableCell align="right">Zysk</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loc.by_lesson_type.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">Brak danych</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        loc.by_lesson_type.map((lt) => (
                          <TableRow key={lt.lesson_type_id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {lt.lesson_type_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{lt.lesson_count}</TableCell>
                            <TableCell align="right">{formatCurrency(lt.single_entry_revenue)}</TableCell>
                            <TableCell align="right">{lt.single_entry_count}</TableCell>
                            <TableCell align="right">{formatCurrency(lt.pass_revenue)}</TableCell>
                            <TableCell align="right">{lt.pass_attendances}</TableCell>
                            <TableCell align="right">{formatCurrency(lt.trainer_cost)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: profitColor(lt.profit) }}
                              >
                                {formatCurrency(lt.profit)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Per trainer table */}
              {mode === "trainers" && (
                <TableContainer>
                  <Table size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Trener</TableCell>
                        <TableCell align="right">Zajęcia</TableCell>
                        <TableCell align="right">Jed. (PLN)</TableCell>
                        <TableCell align="right">Jed. (szt.)</TableCell>
                        <TableCell align="right">Karnety (PLN)</TableCell>
                        <TableCell align="right">Karnety (szt.)</TableCell>
                        <TableCell align="right">Koszt trenera</TableCell>
                        <TableCell align="right">Zysk</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loc.by_trainer.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary">Brak danych</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        loc.by_trainer.map((tr) => (
                          <TableRow key={tr.trainer_id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {tr.trainer_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{tr.lesson_count}</TableCell>
                            <TableCell align="right">{formatCurrency(tr.single_entry_revenue)}</TableCell>
                            <TableCell align="right">{tr.single_entry_count}</TableCell>
                            <TableCell align="right">{formatCurrency(tr.pass_revenue)}</TableCell>
                            <TableCell align="right">{tr.pass_attendances}</TableCell>
                            <TableCell align="right">{formatCurrency(tr.trainer_cost)}</TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: profitColor(tr.profit) }}
                              >
                                {formatCurrency(tr.profit)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};

export default LocationProfitStatistics;
